import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  StarIcon,
  ClockIcon,
  UserIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import {
  DocumentTextIcon as DocumentTextIconSolid,
  FolderIcon as FolderIconSolid,
  StarIcon as StarIconSolid,
} from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const DocumentLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFileType, setSelectedFileType] = useState('all');
  const [sortBy, setSortBy] = useState('lastModified');
  const [viewMode, setViewMode] = useState('list');

  // Document data
  const documents = [
    {
      id: 1,
      name: "Healthcare Policy Updates Q3 2025",
      type: "PDF",
      size: "2.4 MB",
      lastModified: "2025-07-23T14:30:00Z",
      author: "Policy Team",
      category: "Policies & Procedures",
      description: "Comprehensive updates to healthcare policies for the third quarter of 2025, including new compliance requirements.",
      tags: ["Policy", "Healthcare", "Compliance", "Q3"],
      downloads: 245,
      isStarred: true,
      isShared: true,
      version: "v2.1",
      thumbnail: "/images/pdf-thumbnail.png",
      path: "/documents/policies/healthcare-policy-updates-q3-2025.pdf",
    },
    {
      id: 2,
      name: "Equipment Maintenance Schedule",
      type: "Excel",
      size: "156 KB",
      lastModified: "2025-07-22T09:15:00Z",
      author: "Facilities Team",
      category: "Equipment Requests",
      description: "Monthly maintenance schedule for all medical equipment across departments.",
      tags: ["Equipment", "Maintenance", "Schedule", "Monthly"],
      downloads: 89,
      isStarred: false,
      isShared: true,
      version: "v1.3",
      thumbnail: "/images/excel-thumbnail.png",
      path: "/documents/facilities/equipment-maintenance-schedule.xlsx",
    },
    {
      id: 3,
      name: "Training Module - Emergency Procedures",
      type: "Video",
      size: "45.2 MB",
      lastModified: "2025-07-20T16:45:00Z",
      author: "Training Department",
      category: "Training Materials",
      description: "Comprehensive training video covering emergency response procedures for all staff members.",
      tags: ["Training", "Emergency", "Procedures", "Safety"],
      downloads: 178,
      isStarred: true,
      isShared: false,
      version: "v1.0",
      thumbnail: "/images/video-thumbnail.png",
      path: "/documents/training/emergency-procedures-training.mp4",
    },
    {
      id: 4,
      name: "Staff Performance Review Template",
      type: "Word",
      size: "324 KB",
      lastModified: "2025-07-19T11:20:00Z",
      author: "HR Department",
      category: "HR Documents",
      description: "Standardized template for conducting annual staff performance reviews.",
      tags: ["HR", "Performance", "Review", "Template"],
      downloads: 112,
      isStarred: false,
      isShared: true,
      version: "v3.0",
      thumbnail: "/images/word-thumbnail.png",
      path: "/documents/hr/performance-review-template.docx",
    },
    {
      id: 5,
      name: "Monthly Quality Metrics Report",
      type: "PDF",
      size: "1.8 MB",
      lastModified: "2025-07-18T13:45:00Z",
      author: "Quality Assurance Team",
      category: "Quality Reports",
      description: "Detailed analysis of quality metrics and performance indicators for the current month.",
      tags: ["Quality", "Metrics", "Report", "Analysis"],
      downloads: 67,
      isStarred: true,
      isShared: false,
      version: "v1.2",
      thumbnail: "/images/pdf-thumbnail.png",
      path: "/documents/quality/monthly-quality-metrics.pdf",
    },
    {
      id: 6,
      name: "Patient Satisfaction Survey Results",
      type: "PowerPoint",
      size: "3.7 MB",
      lastModified: "2025-07-17T10:30:00Z",
      author: "Patient Experience Team",
      category: "Quality Reports",
      description: "Q2 2025 patient satisfaction survey results with detailed analysis and recommendations.",
      tags: ["Patient", "Satisfaction", "Survey", "Q2"],
      downloads: 134,
      isStarred: false,
      isShared: true,
      version: "v1.1",
      thumbnail: "/images/ppt-thumbnail.png",
      path: "/documents/patient-experience/satisfaction-survey-q2.pptx",
    },
    {
      id: 7,
      name: "Medical Protocol Guidelines 2025",
      type: "PDF",
      size: "5.2 MB",
      lastModified: "2025-07-16T14:15:00Z",
      author: "Medical Director",
      category: "Medical Forms",
      description: "Updated medical protocol guidelines for 2025 with new treatment procedures and standards.",
      tags: ["Medical", "Protocol", "Guidelines", "2025"],
      downloads: 289,
      isStarred: true,
      isShared: true,
      version: "v4.0",
      thumbnail: "/images/pdf-thumbnail.png",
      path: "/documents/medical/protocol-guidelines-2025.pdf",
    },
    {
      id: 8,
      name: "Inventory Management System Manual",
      type: "PDF",
      size: "2.1 MB",
      lastModified: "2025-07-15T09:00:00Z",
      author: "IT Department",
      category: "Training Materials",
      description: "Complete user manual for the new inventory management system implementation.",
      tags: ["Inventory", "Manual", "System", "IT"],
      downloads: 95,
      isStarred: false,
      isShared: false,
      version: "v1.0",
      thumbnail: "/images/pdf-thumbnail.png",
      path: "/documents/it/inventory-system-manual.pdf",
    },
  ];

  // Categories for filtering
  const categories = [
    { id: 'all', name: 'All Categories', count: documents.length, icon: FolderIcon },
    { id: 'hr-documents', name: 'HR Documents', count: documents.filter(d => d.category === 'HR Documents').length, icon: DocumentTextIcon },
    { id: 'medical-forms', name: 'Medical Forms', count: documents.filter(d => d.category === 'Medical Forms').length, icon: DocumentTextIcon },
    { id: 'training-materials', name: 'Training Materials', count: documents.filter(d => d.category === 'Training Materials').length, icon: DocumentTextIcon },
    { id: 'policies-procedures', name: 'Policies & Procedures', count: documents.filter(d => d.category === 'Policies & Procedures').length, icon: DocumentTextIcon },
    { id: 'equipment-requests', name: 'Equipment Requests', count: documents.filter(d => d.category === 'Equipment Requests').length, icon: DocumentTextIcon },
    { id: 'quality-reports', name: 'Quality Reports', count: documents.filter(d => d.category === 'Quality Reports').length, icon: DocumentTextIcon },
  ];

  // File types for filtering
  const fileTypes = [
    { id: 'all', name: 'All Types' },
    { id: 'pdf', name: 'PDF Documents' },
    { id: 'excel', name: 'Excel Files' },
    { id: 'word', name: 'Word Documents' },
    { id: 'powerpoint', name: 'PowerPoint' },
    { id: 'video', name: 'Videos' },
  ];

  // Filter and sort documents
  const filteredDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || 
        doc.category.toLowerCase().replace(/[^a-z0-9]/g, '-') === selectedCategory;
      
      const matchesFileType = selectedFileType === 'all' || 
        doc.type.toLowerCase() === selectedFileType;
      
      return matchesSearch && matchesCategory && matchesFileType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastModified':
          return new Date(b.lastModified) - new Date(a.lastModified);
        case 'size':
          return parseFloat(b.size) - parseFloat(a.size);
        case 'downloads':
          return b.downloads - a.downloads;
        case 'author':
          return a.author.localeCompare(b.author);
        default:
          return 0;
      }
    });

  // Statistics
  const stats = {
    total: documents.length,
    starred: documents.filter(d => d.isStarred).length,
    shared: documents.filter(d => d.isShared).length,
    totalSize: documents.reduce((sum, doc) => sum + parseFloat(doc.size), 0).toFixed(1),
  };

  // Helper functions
  const getFileTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf': return '📄';
      case 'excel': return '📊';
      case 'word': return '📝';
      case 'powerpoint': return '📽️';
      case 'video': return '🎥';
      default: return '📄';
    }
  };

  const getFileTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf': return 'text-red-600 bg-red-50';
      case 'excel': return 'text-green-600 bg-green-50';
      case 'word': return 'text-blue-600 bg-blue-50';
      case 'powerpoint': return 'text-orange-600 bg-orange-50';
      case 'video': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (size) => {
    return size;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Document Library</h1>
              <p className="text-gray-600 mt-1 text-sm">
                Access, manage, and share all your documents
              </p>
            </div>
            <Link
              to="/documents/upload"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Upload Document</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border">
            <div className="flex items-center">
              <DocumentTextIconSolid className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <StarIconSolid className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Starred</p>
                <p className="text-2xl font-bold text-gray-900">{stats.starred}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <ShareIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Shared</p>
                <p className="text-2xl font-bold text-gray-900">{stats.shared}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <FolderIconSolid className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Size</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSize} MB</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
              </div>
              <div className="p-6">
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                        selectedCategory === category.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <category.icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{category.name}</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filters and Search */}
            <div className="bg-white rounded-lg shadow-sm border mb-6">
              <div className="p-6 border-b">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                    {/* Search */}
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search documents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                      />
                    </div>

                    {/* File Type Filter */}
                    <div className="relative">
                      <select
                        value={selectedFileType}
                        onChange={(e) => setSelectedFileType(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {fileTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                      <FunnelIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* Sort */}
                    <div className="relative">
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="lastModified">Sort by Date</option>
                        <option value="name">Sort by Name</option>
                        <option value="size">Sort by Size</option>
                        <option value="downloads">Sort by Downloads</option>
                        <option value="author">Sort by Author</option>
                      </select>
                      <ArrowsUpDownIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 text-sm font-medium transition-colors ${
                          viewMode === 'list'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        List
                      </button>
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-2 text-sm font-medium transition-colors ${
                          viewMode === 'grid'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Grid
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Count */}
              <div className="px-6 py-3 bg-gray-50 border-b">
                <p className="text-sm text-gray-600">
                  Showing {filteredDocuments.length} of {documents.length} documents
                </p>
              </div>
            </div>

            {/* Documents Content */}
            {filteredDocuments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
                <p className="text-gray-500 mb-6">
                  Try adjusting your filters or search criteria to find documents.
                </p>
                <Link
                  to="/documents/upload"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Upload New Document
                </Link>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                {filteredDocuments.map((document) => (
                  <div
                    key={document.id}
                    className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow ${
                      viewMode === 'list' ? 'p-6' : 'p-4'
                    }`}
                  >
                    {viewMode === 'list' ? (
                      // List View
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="flex-shrink-0">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${getFileTypeColor(document.type)}`}>
                              {getFileTypeIcon(document.type)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <Link
                                to={`/documents/${document.id}`}
                                className="text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors truncate"
                              >
                                {document.name}
                              </Link>
                              {document.isStarred && (
                                <StarIconSolid className="h-4 w-4 text-yellow-500" />
                              )}
                              {document.isShared && (
                                <ShareIcon className="h-4 w-4 text-green-500" />
                              )}
                            </div>
                            
                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{document.description}</p>
                            
                            <div className="flex items-center space-x-6 text-sm text-gray-500 mb-3">
                              <div className="flex items-center">
                                <UserIcon className="h-4 w-4 mr-1" />
                                <span>{document.author}</span>
                              </div>
                              <div className="flex items-center">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                <span>{formatDate(document.lastModified)}</span>
                              </div>
                              <div>
                                <span>{formatFileSize(document.size)}</span>
                              </div>
                              <div>
                                <span>{document.downloads} downloads</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getFileTypeColor(document.type)}`}>
                                {document.type}
                              </span>
                              <span className="text-xs text-gray-500">{document.version}</span>
                              <div className="flex flex-wrap gap-1">
                                {document.tags.slice(0, 3).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="ml-4 flex items-center space-x-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                            <StarIcon className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                            <ShareIcon className="h-4 w-4" />
                          </button>
                          <Link
                            to={`/documents/${document.id}`}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    ) : (
                      // Grid View
                      <div>
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${getFileTypeColor(document.type)}`}>
                            {getFileTypeIcon(document.type)}
                          </div>
                          <div className="flex items-center space-x-1">
                            {document.isStarred && (
                              <StarIconSolid className="h-4 w-4 text-yellow-500" />
                            )}
                            {document.isShared && (
                              <ShareIcon className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        </div>
                        
                        <Link
                          to={`/documents/${document.id}`}
                          className="block text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors mb-2 line-clamp-2"
                        >
                          {document.name}
                        </Link>
                        
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{document.description}</p>
                        
                        <div className="space-y-2 text-xs text-gray-500 mb-4">
                          <div className="flex items-center">
                            <UserIcon className="h-3 w-3 mr-1" />
                            <span>{document.author}</span>
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            <span>{formatDate(document.lastModified)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{formatFileSize(document.size)}</span>
                            <span>{document.downloads} downloads</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mb-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getFileTypeColor(document.type)}`}>
                            {document.type}
                          </span>
                          <span className="text-xs text-gray-500">{document.version}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex flex-wrap gap-1">
                            {document.tags.slice(0, 2).map((tag, index) => (
                              <span
                                key={index}
                                className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                              <ArrowDownTrayIcon className="h-3 w-3" />
                            </button>
                            <Link
                              to={`/documents/${document.id}`}
                              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <EyeIcon className="h-3 w-3" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DocumentLibrary;
